/**
 * Modern Header Scroll Controller
 * Handles show/hide behavior for header on scroll with advanced state management
 */

class HeaderScrollController {
  constructor(options = {}) {
    // Configuration with defaults
    this.config = {
      headerSelector: '.site-header',
      scrollThreshold: 1,
      hideDelay: 0,
      showDelay: 0,
      disableOnMobile: false,
      mobileBreakpoint: 768,
      scrolledThreshold: 50, // Scroll distance to add 'scrolled' class (show background on non-floating header)
      ...options
    };

    // State management
    this.state = {
      lastScrollY: 0,
      currentScrollY: 0,
      isScrollingDown: false,
      isHeaderVisible: true,
      isHeaderHidden: false,
      isDragging: false,
      isAtTop: true,
      drawerIsOpen: false,
      megaMenuIsOpen: false,
      isDisabled: false,
      isInitialized: false,
      headerHeight: 0,
      isInitialLoad: true, // New property to protect initial load
      scrollDownStartY: 0, // Track where current scroll down began
      wasScrollingDown: false, // Track previous scroll direction
      isHomePage: false, // Track if we're on home page (transparent header)
      isScrolled: false // Track if scrolled past threshold for background
    };

    // Performance optimization
    this.rafId = null;
    this.timeouts = {
      hide: null,
      show: null,
      debounce: null,
      initialLoad: null // Timeout for initial protection
    };

    // DOM references
    this.header = null;
    this.body = document.body;

    // Bind methods
    this.handleScroll = this.handleScroll.bind(this);
    this.handleResize = this.handleResize.bind(this);
    this.updateHeader = this.updateHeader.bind(this);
    this.onDrawerOpen = this.onDrawerOpen.bind(this);
    this.onDrawerClose = this.onDrawerClose.bind(this);
    this.onMegaMenuOpen = this.onMegaMenuOpen.bind(this);
    this.onMegaMenuClose = this.onMegaMenuClose.bind(this);

    this.init();
  }

  init() {
    this.header = document.querySelector(this.config.headerSelector);
    this.announcementBar = document.querySelector('[data-announcement-bar]');
    
    if (!this.header) {
      return;
    }

    // Check if we're on home page (has-transparent-header on body)
    this.state.isHomePage = document.body.classList.contains('has-transparent-header');
    
    // Check if hide on scroll is enabled via data attribute
    const hideOnScroll = this.header.dataset.hideOnScroll === 'true';
    
    // Header hide is disabled only if hide_on_scroll setting is OFF
    // Note: On home page, header CAN hide if the setting is enabled
    this.state.isDisabled = !hideOnScroll;

    this.setupInitialState();
    this.bindEvents();
    this.state.isInitialized = true;
    
    // Validate integration
    this.validateIntegration();

    // Emit initialization event
    this.emitEvent('headerScrollInit', { controller: this });
  }

  setupInitialState() {
    this.state.currentScrollY = window.scrollY;
    this.state.lastScrollY = this.state.currentScrollY;
    this.state.isAtTop = this.state.currentScrollY <= this.config.scrollThreshold;
    
    // IMPORTANT: Header must always be visible on initial load
    this.state.isHeaderVisible = true;
    this.state.isHeaderHidden = false;
    
    // Check actual drawer state from body classes and overlay states
    const mobileMenuOverlay = document.getElementById('mobile-menu-overlay');
    const isMobileMenuOpen = mobileMenuOverlay && mobileMenuOverlay.getAttribute('aria-hidden') === 'false';
    
    this.state.drawerIsOpen = document.body.classList.contains('drawer-open') || 
                              document.body.classList.contains('mobile-menu-open') ||
                              isMobileMenuOpen;
    
    // Set up header height compensation
    this.setupHeaderHeightCompensation();
    
    // Set initial classes - ENSURE visibility
    this.updateHeaderClasses();
    
    // Force visible state immediately
    if (this.header) {
      this.header.style.transform = 'translateY(0)';
      this.header.classList.add('site-header--force-visible');
    }
    
    // Protect the first 2 seconds to prevent premature hiding
    this.timeouts.initialLoad = setTimeout(() => {
      this.state.isInitialLoad = false;
    }, 2000);
  }

  bindEvents() {
    // Scroll event with passive listener for better performance
    window.addEventListener('scroll', this.handleScroll, { passive: true });
    
    // Resize event
    window.addEventListener('resize', this.handleResize, { passive: true });
    
    // Drawer events
    document.addEventListener('drawerOpen', this.onDrawerOpen);
    document.addEventListener('drawerClose', this.onDrawerClose);
    document.addEventListener('openDrawer', this.onDrawerOpen);
    
    // Mobile menu events
    document.addEventListener('mobileMenuOpen', this.onDrawerOpen);
    document.addEventListener('mobileMenuClose', this.onDrawerClose);

    // Mega menu events (if they exist)
    document.addEventListener('megaMenuOpen', this.onMegaMenuOpen);
    document.addEventListener('megaMenuClose', this.onMegaMenuClose);

    // Body class changes (for drawer states)
    this.observeBodyClasses();
    
    // Header height changes
    this.observeHeaderHeight();
  }

  handleScroll() {
    if (this.rafId) return;
    
    this.rafId = requestAnimationFrame(() => {
      this.updateScrollState();
      this.updateHeader();
      this.updateScrolledState(); // Update scrolled class for background
      this.rafId = null;
    });
  }
  
  // Update scrolled state for showing background on transparent header
  updateScrolledState() {
    if (!this.header) return;
    
    const shouldBeScrolled = this.state.currentScrollY >= this.config.scrolledThreshold;
    
    if (shouldBeScrolled !== this.state.isScrolled) {
      this.state.isScrolled = shouldBeScrolled;
      
      if (shouldBeScrolled) {
        this.header.classList.add('scrolled');
      } else {
        this.header.classList.remove('scrolled');
      }
    }
  }

  updateScrollState() {
    this.state.currentScrollY = Math.max(0, window.scrollY);
    
    const scrollDifference = this.state.currentScrollY - this.state.lastScrollY;
    const isScrollingDown = scrollDifference > 0;
    const hasScrolledEnough = Math.abs(scrollDifference) >= this.config.scrollThreshold;
    
    // Track when scroll direction changes to down
    if (isScrollingDown && !this.state.wasScrollingDown) {
      // Just started scrolling down - record the start position
      this.state.scrollDownStartY = this.state.currentScrollY;
    }
    
    // Always update direction if there's any movement
    if (scrollDifference !== 0) {
      this.state.wasScrollingDown = this.state.isScrollingDown;
      this.state.isScrollingDown = isScrollingDown;
    }
    
    // Update last position only if threshold is met
    if (hasScrolledEnough) {
      this.state.lastScrollY = this.state.currentScrollY;
    }
    
    // Update top state
    this.state.isAtTop = this.state.currentScrollY <= this.config.scrollThreshold;
  }

  updateHeader() {
    const shouldHide = this.shouldHideHeader();
    const shouldShow = this.shouldShowHeader();

    if (shouldHide && this.state.isHeaderVisible) {
      this.hideHeader();
    } else if (shouldShow && !this.state.isHeaderVisible) {
      this.showHeader();
    }
  }

  shouldHideHeader() {
    // Require at least 50px of scroll from where scroll down started
    const minScrollDistance = 50;
    const scrolledDistance = this.state.currentScrollY - this.state.scrollDownStartY;
    const hasScrolledEnoughToHide = scrolledDistance >= minScrollDistance;
    
    const result = (
      !this.state.isDisabled &&
      !this.state.isInitialLoad &&
      !this.state.isAtTop &&
      this.state.isScrollingDown &&
      this.state.isHeaderVisible &&
      !this.state.drawerIsOpen &&
      !this.state.megaMenuIsOpen &&
      !this.isMobileAndDisabled() &&
      hasScrolledEnoughToHide
    );
    
    return result;
  }

  shouldShowHeader() {
    // Note: Don't check isDisabled here - we always want to be able to show the header
    return (
      (
        this.state.isAtTop ||
        !this.state.isScrollingDown ||
        this.state.drawerIsOpen ||
        this.state.megaMenuIsOpen ||
        this.state.isDisabled // Always show when hide is disabled
      ) &&
      !this.state.isHeaderVisible
    );
  }

  hideHeader() {
    this.clearTimeouts();
    
    this.timeouts.hide = setTimeout(() => {
      if (this.shouldHideHeader()) {
        this.state.isHeaderVisible = false;
        this.state.isHeaderHidden = true;
        this.updateHeaderClasses();
        this.emitEvent('headerHidden');
      }
    }, this.config.hideDelay);
  }

  showHeader() {
    this.clearTimeouts();
    
    this.timeouts.show = setTimeout(() => {
      this.state.isHeaderVisible = true;
      this.state.isHeaderHidden = false;
      this.updateHeaderClasses();
      this.emitEvent('headerShown');
    }, this.config.showDelay);
  }

  updateHeaderClasses() {
    if (!this.header) return;

    // Remove all scroll-related classes
    this.header.classList.remove(
      'site-header--hidden',
      'site-header--at-top',
      'site-header--drawer-open',
      'site-header--mega-menu-open'
    );

    // Add current state classes
    if (this.state.isAtTop) {
      this.header.classList.add('site-header--at-top');
    }

    if (this.state.drawerIsOpen) {
      this.header.classList.add('site-header--drawer-open');
    }

    if (this.state.megaMenuIsOpen) {
      this.header.classList.add('site-header--mega-menu-open');
    }

    // Add hidden class when header should be hidden (and hiding is enabled)
    if (this.state.isHeaderHidden && !this.state.isDisabled) {
      this.header.classList.add('site-header--hidden');
    }
    
    // Sync announcement bar with header state
    this.updateAnnouncementBar();
  }
  
  updateAnnouncementBar() {
    if (!this.announcementBar) return;
    
    // Check if announcement bar has hide on scroll enabled
    if (this.announcementBar.dataset.hideOnScroll !== 'true') return;
    
    if (this.state.isHeaderHidden && !this.state.isAtTop) {
      // Hide announcement bar when header hides
      this.announcementBar.classList.add('announcement-bar--hidden');
      this.announcementBar.classList.remove('announcement-bar--visible');
      document.documentElement.classList.add('announcement-hidden');
    } else {
      // Show announcement bar when header shows
      this.announcementBar.classList.remove('announcement-bar--hidden');
      this.announcementBar.classList.add('announcement-bar--visible');
      document.documentElement.classList.remove('announcement-hidden');
    }
  }

  // Event handlers
  onDrawerOpen() {
    this.state.drawerIsOpen = true;
    this.showHeader();
  }

  onDrawerClose() {
    this.state.drawerIsOpen = false;
    this.updateHeader();
  }

  onMegaMenuOpen() {
    this.state.megaMenuIsOpen = true;
    this.showHeader();
  }

  onMegaMenuClose() {
    this.state.megaMenuIsOpen = false;
    this.updateHeader();
  }

  handleResize() {
    this.clearTimeouts();
    
    this.timeouts.debounce = setTimeout(() => {
      this.setupInitialState();
      // Recalculate header height on resize
      this.setupHeaderHeightCompensation();
    }, 100);
  }

  // Observer for body class changes
  observeBodyClasses() {
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
          const hasDrawerOpen = this.body.classList.contains('drawer-open');
          const hasMobileMenuOpen = this.body.classList.contains('mobile-menu-open');
          
          // Also check mobile menu overlay state
          const mobileMenuOverlay = document.getElementById('mobile-menu-overlay');
          const isMobileMenuOverlayOpen = mobileMenuOverlay && mobileMenuOverlay.getAttribute('aria-hidden') === 'false';
          
          const shouldBeOpen = hasDrawerOpen || hasMobileMenuOpen || isMobileMenuOverlayOpen;
          
          if (shouldBeOpen && !this.state.drawerIsOpen) {
            this.onDrawerOpen();
          } else if (!shouldBeOpen && this.state.drawerIsOpen) {
            this.onDrawerClose();
          }
        }
      });
    });

    observer.observe(this.body, { 
      attributes: true, 
      attributeFilter: ['class'] 
    });
    
    // Also observe mobile menu overlay changes
    const mobileMenuOverlay = document.getElementById('mobile-menu-overlay');
    if (mobileMenuOverlay) {
      const overlayObserver = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.type === 'attributes' && mutation.attributeName === 'aria-hidden') {
            const isOpen = mobileMenuOverlay.getAttribute('aria-hidden') === 'false';
            if (isOpen && !this.state.drawerIsOpen) {
              this.onDrawerOpen();
            } else if (!isOpen && this.state.drawerIsOpen) {
              // Check if any other drawer is still open
              const hasOtherDrawerOpen = this.body.classList.contains('drawer-open');
              if (!hasOtherDrawerOpen) {
                this.onDrawerClose();
              }
            }
          }
        });
      });
      
      overlayObserver.observe(mobileMenuOverlay, {
        attributes: true,
        attributeFilter: ['aria-hidden']
      });
    }
  }

  // Observer for header height changes
  observeHeaderHeight() {
    if (!this.header) return;
    
    // Use ResizeObserver to detect header size changes
    if (window.ResizeObserver) {
      const resizeObserver = new ResizeObserver((entries) => {
        for (const entry of entries) {
          const newHeight = entry.contentRect.height;
          if (newHeight !== this.state.headerHeight) {
            this.setupHeaderHeightCompensation();
          }
        }
      });
      
      resizeObserver.observe(this.header);
      
      // Store observer for cleanup
      this.headerResizeObserver = resizeObserver;
    }
  }

  // Header height compensation
  setupHeaderHeightCompensation() {
    if (!this.header) return;
    
    // Get the actual height of the header
    const headerHeight = this.header.offsetHeight;
    
    // Find main content element (try different selectors)
    const mainElement = document.querySelector('main') || 
                       document.querySelector('.main-content') ||
                       document.querySelector('#main') ||
                       document.querySelector('[role="main"]');
    
    if (!mainElement) {
      return;
    }
    
    // Set CSS custom property for header height
    document.documentElement.style.setProperty('--header-height', `${headerHeight}px`);
    
    // Add class to body to indicate dynamic header height is active
    document.body.classList.add('header-height-dynamic');
    
    // Store height in state for reference
    this.state.headerHeight = headerHeight;
  }

  // Utility methods
  isMobileAndDisabled() {
    return this.config.disableOnMobile && window.innerWidth <= this.config.mobileBreakpoint;
  }
  
  validateIntegration() {
    const issues = [];
    
    // Check CSS classes
    if (!this.header.classList.length) {
      issues.push('Header has no CSS classes - check if styles are loaded');
    }
    
    // Check mobile menu overlay
    const mobileMenuOverlay = document.getElementById('mobile-menu-overlay');
    if (!mobileMenuOverlay) {
      issues.push('Mobile menu overlay not found - mobile menu events may not work');
    }
    
    // Check if drawer.js is loaded by looking for drawer events
    const hasDrawerListeners = document.querySelector('[data-drawer]') || 
                              document.querySelector('.js-drawer-open');
    
    if (issues.length > 0) {
      // Integration issues found - silently noted
    }
  }

  clearTimeouts() {
    Object.values(this.timeouts).forEach(timeout => {
      if (timeout) clearTimeout(timeout);
    });
    this.timeouts = { hide: null, show: null, debounce: null, initialLoad: null };
  }

  emitEvent(eventName, detail = {}) {
    const event = new CustomEvent(eventName, {
      detail: {
        ...detail,
        scrollY: this.state.currentScrollY,
        isHeaderVisible: this.state.isHeaderVisible,
        state: { ...this.state }
      }
    });
    document.dispatchEvent(event);
  }

  // Public API
  enable() {
    this.state.isDisabled = false;
    this.updateHeader();
  }

  disable() {
    this.state.isDisabled = true;
    this.showHeader();
  }

  forceShow() {
    this.clearTimeouts();
    this.showHeader();
  }

  forceHide() {
    if (!this.state.drawerIsOpen && !this.state.megaMenuIsOpen && !this.state.isAtTop) {
      this.clearTimeouts();
      this.hideHeader();
    }
  }

  getState() {
    return { ...this.state };
  }

  // Recalculate header height (useful for dynamic content changes)
  recalculateHeaderHeight() {
    this.setupHeaderHeightCompensation();
  }

  // Debug method - call from browser console
  debug() {
    return {
      header: this.header,
      headerClasses: this.header ? Array.from(this.header.classList) : [],
      headerHeight: this.state.headerHeight,
      cssHeaderHeight: getComputedStyle(document.documentElement).getPropertyValue('--header-height'),
      config: this.config,
      state: this.state,
      scrollY: window.scrollY,
      shouldHide: this.shouldHideHeader(),
      shouldShow: this.shouldShowHeader()
    };
  }

  // Force reset state
  resetState() {
    this.state.drawerIsOpen = false;
    this.state.megaMenuIsOpen = false;
    this.state.isHeaderVisible = true;
    this.state.isHeaderHidden = false;
    this.updateHeaderClasses();
  }

  // Test scroll behavior
  testScrollDown() {
    // Force conditions for hiding
    this.state.isScrollingDown = true;
    this.state.isAtTop = false;
    this.state.isHeaderVisible = true;
    this.state.drawerIsOpen = false;
    this.state.megaMenuIsOpen = false;
    this.state.isDisabled = false;
    
    if (this.shouldHideHeader()) {
      this.hideHeader();
    }
  }

  destroy() {
    this.clearTimeouts();
    
    if (this.rafId) {
      cancelAnimationFrame(this.rafId);
    }
    
    // Clean up resize observer
    if (this.headerResizeObserver) {
      this.headerResizeObserver.disconnect();
    }

    window.removeEventListener('scroll', this.handleScroll);
    window.removeEventListener('resize', this.handleResize);
    document.removeEventListener('drawerOpen', this.onDrawerOpen);
    document.removeEventListener('drawerClose', this.onDrawerClose);
    document.removeEventListener('openDrawer', this.onDrawerOpen);
    document.removeEventListener('mobileMenuOpen', this.onDrawerOpen);
    document.removeEventListener('mobileMenuClose', this.onDrawerClose);
    document.removeEventListener('megaMenuOpen', this.onMegaMenuOpen);
    document.removeEventListener('megaMenuClose', this.onMegaMenuClose);

    this.emitEvent('headerScrollDestroy');
  }
}

// Auto-initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  // Initialize with default configuration
  window.headerScrollController = new HeaderScrollController({
    scrollThreshold: 1,
    hideDelay: 0,
    showDelay: 0
  });
  
  // Expose debug function globally
  window.debugHeaderScroll = () => window.headerScrollController.debug();
  
  // Expose manual control functions
  window.hideHeader = () => window.headerScrollController.forceHide();
  window.showHeader = () => window.headerScrollController.forceShow();
  window.resetHeaderState = () => window.headerScrollController.resetState();
  window.recalculateHeaderHeight = () => window.headerScrollController.recalculateHeaderHeight();
});

// Export for use in other modules
if (typeof module !== 'undefined' && module.exports) {
  module.exports = HeaderScrollController;
}
